import React, { useEffect, useState } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import api from "../api";
import bgVideo from "../assets/lbg.mp4";

/* ================= ICONS ================= */
const dotIcon = (color) =>
  L.divIcon({
    className: "",
    html: `<span style="display:block;width:16px;height:16px;border-radius:9999px;background:${color};border:2px solid #fff;box-shadow:0 0 0 2px rgba(15,23,42,0.35);"></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

const vacantIcon = dotIcon("#14b8a6");
const occupiedIcon = dotIcon("#f97316");
const selectedIcon = dotIcon("#38bdf8");

const asArray = (payload) =>
  Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.items)
    ? payload.items
    : Array.isArray(payload?.results)
    ? payload.results
    : [];

const hasValidCoordinates = (latitude, longitude) => {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
  if (lat === 0 && lng === 0) return false;
  return true;
};

const formatCoordinatePair = (latitude, longitude) => {
  if (!hasValidCoordinates(latitude, longitude)) return "Not set";
  return `${Number(latitude).toFixed(6)}, ${Number(longitude).toFixed(6)}`;
};

/* ================= MAP HELPERS ================= */
function FlyTo({ target }) {
  const map = useMap();

  useEffect(() => {
    if (
      target &&
      Number.isFinite(target.latitude) &&
      Number.isFinite(target.longitude)
    ) {
      map.flyTo(
        [target.latitude, target.longitude],
        17,
        { animate: true, duration: 1.2 }
      );
    }
  }, [target, map]);

  return null;
}

function MapClick({ onClick }) {
  useMapEvents({
    click(e) {
      const lat = Number(e.latlng?.lat);
      const lng = Number(e.latlng?.lng);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        onClick({ lat, lng });
      }
    },
  });
  return null;
}

/* ================= MAIN ================= */
export default function MapProperties() {

  const [properties, setProperties] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [zoomTarget, setZoomTarget] = useState(null);
  const [markerFilter, setMarkerFilter] = useState("all");

  const [formData, setFormData] = useState({
    property_name: "",
    location: "",
    rent_price: "",
    description: "",
    tenant_name: "",
    is_occupied: false,
  });

  /* ================= LOAD ================= */
  useEffect(() => {
    loadProperties();
  }, []);

  const loadProperties = async () => {
    try {
      const res = await api.get("/rental-properties");
      setProperties(asArray(res.data));
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= HELPERS ================= */
  const resetForm = () => {
    setFormData({
      property_name: "",
      location: "",
      rent_price: "",
      description: "",
      tenant_name: "",
      is_occupied: false,
    });
  };

  const handleMapClick = (pos) => {
    if (editingProperty) {
      setEditingProperty((prev) =>
        prev
          ? {
              ...prev,
              latitude: pos.lat,
              longitude: pos.lng,
            }
          : prev
      );
      setZoomTarget({ latitude: pos.lat, longitude: pos.lng });
      return;
    }

    setEditingProperty(null);
    setZoomTarget(null);
    setSelectedPosition(pos);
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* ================= CRUD ================= */
  const saveProperty = async () => {
    if (
      !selectedPosition ||
      !Number.isFinite(selectedPosition.lat) ||
      !Number.isFinite(selectedPosition.lng)
    ) {
      alert("Click on the map to select a location");
      return;
    }

    if (!formData.property_name.trim()) {
      alert("Property name is required");
      return;
    }

    try {
      await api.post("/rental-properties", {
        ...formData,
        latitude: selectedPosition.lat,
        longitude: selectedPosition.lng,
      });

      await loadProperties();
      setSelectedPosition(null);
      resetForm();
    } catch (err) {
      console.error(err);
      alert("Failed to save property");
    }
  };

  const updateProperty = async () => {
    if (
      !editingProperty ||
      !hasValidCoordinates(editingProperty.latitude, editingProperty.longitude)
    ) {
      alert("Click on the map to set a valid latitude/longitude first.");
      return;
    }

    try {
      await api.put(`/rental-properties/${editingProperty.id}`, {
        ...formData,
        latitude: Number(editingProperty.latitude),
        longitude: Number(editingProperty.longitude),
      });

      await loadProperties();
      setEditingProperty(null);
      setZoomTarget(null);
      resetForm();
    } catch (err) {
      console.error(err);
      alert("Failed to update property");
    }
  };

  const openEdit = (p) => {
    const lat = Number(p.latitude);
    const lng = Number(p.longitude);

    if (!hasValidCoordinates(lat, lng)) return;

    setSelectedPosition(null);
    setEditingProperty(p);
    setZoomTarget({ latitude: lat, longitude: lng });

    setFormData({
      property_name: p.property_name || "",
      location: p.location || "",
      rent_price: p.rent_price || "",
      description: p.description || "",
      tenant_name: p.tenant_name || "",
      is_occupied:
        p.is_occupied === true ||
        p.is_occupied === 1 ||
        p.is_occupied === "1",
    });
  };

  const openCoordinateFix = (p) => {
    setSelectedPosition(null);
    setEditingProperty(p);
    setZoomTarget(
      hasValidCoordinates(p.latitude, p.longitude)
        ? { latitude: Number(p.latitude), longitude: Number(p.longitude) }
        : null
    );

    setFormData({
      property_name: p.property_name || "",
      location: p.location || "",
      rent_price: p.rent_price || "",
      description: p.description || "",
      tenant_name: p.tenant_name || "",
      is_occupied:
        p.is_occupied === true ||
        p.is_occupied === 1 ||
        p.is_occupied === "1",
    });
  };

  const showVacantProperties = () => {
    setEditingProperty(null);
    setSelectedPosition(null);
    setZoomTarget(null);
    resetForm();
  };

  /* ================= VACANT LIST (FINAL FIX) ================= */
  const vacantProperties = properties.filter((p) => {
    const occupied =
      p.is_occupied === true ||
      p.is_occupied === 1 ||
      p.is_occupied === "1";

    return !occupied && hasValidCoordinates(p.latitude, p.longitude);
  });

  const mappedProperties = properties.filter(
    (p) => hasValidCoordinates(p.latitude, p.longitude)
  );
  const missingCoordinateProperties = properties.filter(
    (p) => !hasValidCoordinates(p.latitude, p.longitude)
  );

  const filteredMarkers = mappedProperties.filter((p) => {
    if (markerFilter === "all") return true;
    const occupied =
      p.is_occupied === true ||
      p.is_occupied === 1 ||
      p.is_occupied === "1";
    return markerFilter === "occupied" ? occupied : !occupied;
  });

  /* ================= RENDER ================= */
  return (
    <div className="relative w-full min-h-screen overflow-hidden bg-slate-950">
      <video data-workspace-background="true" aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
        src={bgVideo}
        autoPlay
        muted
        loop
      />

      <div className="absolute inset-0 bg-slate-950/85"></div>

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-6 text-white sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
              Property Map
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-wide text-white md:text-4xl">
              View Properties Map
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
              Review property locations, select a map point, and open vacant
              listings without leaving the admin workspace.
            </p>
          </div>

          
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
          {/* MAP */}
          <div className="overflow-hidden rounded-lg border border-white/10 bg-slate-950/80 p-2 shadow-2xl">
            <div className="mb-2 flex flex-wrap gap-2 px-1 pt-1">
              {[
                ["all", "All Markers"],
                ["vacant", "Vacant Only"],
                ["occupied", "Occupied Only"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setMarkerFilter(key)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                    markerFilter === key
                      ? "bg-teal-600 text-white"
                      : "border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <MapContainer
              key="main-map"
              center={[14.1499, 121.3152]}
              zoom={14}
              className="h-[520px] w-full rounded-md xl:h-[calc(100vh-210px)] xl:min-h-[560px]"
            >
              <TileLayer
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                maxZoom={19}
              />

              <MapClick onClick={handleMapClick} />
              <FlyTo target={zoomTarget} />

              {/* EXISTING MARKERS */}
              {filteredMarkers.map((p) => {
                  const occupied =
                    p.is_occupied === true ||
                    p.is_occupied === 1 ||
                    p.is_occupied === "1";

                  return (
                    <Marker
                      key={p.id}
                      position={[
                        Number(p.latitude),
                        Number(p.longitude),
                      ]}
                      icon={occupied ? occupiedIcon : vacantIcon}
                      eventHandlers={{ click: () => openEdit(p) }}
                    />
                  );
                })}

              {/* PREVIEW MARKER */}
              {selectedPosition && (
                <Marker
                  position={[
                    selectedPosition.lat,
                    selectedPosition.lng,
                  ]}
                  icon={selectedIcon}
                />
              )}
            </MapContainer>
            {mappedProperties.length === 0 ? (
              <p className="px-2 py-2 text-xs text-amber-200">
                No markers yet. Add latitude/longitude by clicking the map and saving a property.
              </p>
            ) : null}
          </div>

          {/* SIDE PANEL */}
          <div className="rounded-lg border border-white/10 bg-slate-950/90 p-5 shadow-2xl xl:h-[calc(100vh-210px)] xl:min-h-[560px] xl:overflow-hidden">
            <div className="mb-5 border-b border-white/10 pb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
                Map Tools
              </p>
              <h3 className="mt-2 text-xl font-bold text-white">
                {editingProperty
                  ? "Edit Property"
                  : selectedPosition
                  ? "Add Property"
                  : "Vacant Properties"}
              </h3>
            </div>

            {(editingProperty || selectedPosition) ? (
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={showVacantProperties}
                  className="mb-1 w-fit rounded-lg border border-white/15 px-3 py-2 text-sm font-semibold text-slate-100 transition hover:border-teal-300/60 hover:bg-teal-500/15 hover:text-teal-100"
                >
                  Back to Vacant Properties
                </button>

                <input
                  className="rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                  placeholder="Property name"
                  name="property_name"
                  value={formData.property_name}
                  onChange={handleChange}
                />

                <input
                  className="rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                  placeholder="Location"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                />

                <input
                  type="number"
                  className="rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                  placeholder="Rent price"
                  name="rent_price"
                  value={formData.rent_price}
                  onChange={handleChange}
                />

                <textarea
                  rows={2}
                  className="rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                  placeholder="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />

                <label className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-200">
                  <input
                    type="checkbox"
                    name="is_occupied"
                    checked={formData.is_occupied}
                    onChange={handleChange}
                  />
                  Occupied
                </label>

                {formData.is_occupied && (
                  <input
                    className="rounded-lg border border-white/10 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
                    placeholder="Tenant name"
                    name="tenant_name"
                    value={formData.tenant_name}
                    onChange={handleChange}
                  />
                )}

                <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-slate-300">
                  Coordinates:{" "}
                  {editingProperty
                    ? formatCoordinatePair(editingProperty.latitude, editingProperty.longitude)
                    : `${selectedPosition.lat.toFixed(6)}, ${selectedPosition.lng.toFixed(6)}`}
                </div>

                <button
                  onClick={editingProperty ? updateProperty : saveProperty}
                  className="mt-2 rounded-lg bg-teal-600 py-3 font-semibold text-white transition hover:bg-teal-500"
                >
                  {editingProperty ? "Update Property" : "Save Property"}
                </button>
              </div>
            ) : (
              <div className="space-y-3 xl:max-h-[calc(100vh-330px)] xl:overflow-y-auto xl:pr-1">
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                    Vacant With Coordinates
                  </p>
                  {vacantProperties.length > 0 ? (
                    vacantProperties.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => openEdit(p)}
                        className="mb-2 w-full rounded-lg border border-white/10 bg-white/5 p-4 text-left transition hover:border-teal-300/50 hover:bg-teal-500/10"
                      >
                        <div className="font-semibold text-white">{p.property_name}</div>
                        <div className="mt-1 text-xs text-slate-300">{p.location}</div>
                      </button>
                    ))
                  ) : (
                    <div className="rounded-lg border border-dashed border-white/15 px-4 py-4 text-center text-sm text-slate-300">
                      No vacant properties
                    </div>
                  )}
                </div>

                <div className="pt-2">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-amber-300">
                    Needs Coordinates
                  </p>
                  {missingCoordinateProperties.length > 0 ? (
                    missingCoordinateProperties.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => openCoordinateFix(p)}
                        className="mb-2 w-full rounded-lg border border-amber-300/30 bg-amber-500/10 p-4 text-left transition hover:bg-amber-500/20"
                      >
                        <div className="font-semibold text-white">{p.property_name}</div>
                        <div className="mt-1 text-xs text-slate-300">{p.location}</div>
                        <div className="mt-1 text-[11px] text-amber-100">
                          Click this, then click map to set exact location
                        </div>
                      </button>
                    ))
                  ) : (
                    <div className="rounded-lg border border-dashed border-white/15 px-4 py-4 text-center text-sm text-slate-300">
                      All properties have coordinates
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
 
